/**
 * One settled swap, as a table row: shared by the live feed and the history on
 * the market page, so the two cannot come to describe the same trade
 * differently.
 */

import { Link } from 'react-router-dom'

import type { IntentsSwap } from '../api/generated/IntentsSwap'
import { ago, price as fmtPrice, tokens, utc, when } from '../lib/format'
import { href } from '../lib/routes'
import { tokenLabel } from '../lib/tokens'

export function SwapRow({
  chain,
  swap,
  asset,
  decimals,
  fresh = false,
  relative = false,
}: {
  /** The chain whose token this is, for the transaction page's address. */
  chain: string
  swap: IntentsSwap
  /** The tracked asset's own symbol. */
  asset: string
  /** What `asset_amount` is scaled by. */
  decimals: number
  /** Flash once on arrival, as the block ticker does. */
  fresh?: boolean
  /** "3m ago" rather than a date — right for a live feed, wrong for history. */
  relative?: boolean
}) {
  const counter = tokenLabel(swap.counter_token, swap.counter_symbol)
  // A side only for a user's trade. Solvers trading with each other have no
  // taker, so there is no side to name — said in words rather than left blank,
  // because a blank reads as "unknown" and this is known.
  const side =
    swap.kind === 'unattributed' ? 'between solvers' : swap.side === 'buy' ? 'bought' : 'sold'
  return (
    <tr className={fresh ? 'fresh' : undefined}>
      <td className="left numeral" title={utc(swap.at)}>
        {relative ? ago(swap.at) : when(swap.at)}
      </td>
      <td className="left">{side}</td>
      <td className="numeral" title={`${swap.asset_amount} base units`}>
        {tokens(swap.asset_amount, decimals)} {asset}
      </td>
      <td className="numeral" title={swap.counter_token}>
        {fmtPrice(swap.price)} {counter}
      </td>
      <td className="left" title={swap.taker ?? swap.accounts.join(', ')}>
        {swap.kind === 'unattributed' ? '—' : (swap.referral ?? '—')}
      </td>
      <td className="left">
        <Link to={href({ chain, nearTx: swap.tx_hash })} title={swap.tx_hash}>
          {swap.tx_hash.slice(0, 8)}…
        </Link>
      </td>
    </tr>
  )
}

/** The header row the swap tables share. */
export function SwapHead({ asset }: { asset: string }) {
  return (
    <thead>
      <tr>
        <th scope="col" className="left">
          Settled
        </th>
        <th scope="col" className="left">
          Taker
        </th>
        <th scope="col">{asset}</th>
        <th scope="col">Price per {asset}</th>
        <th scope="col" className="left">
          Via
        </th>
        <th scope="col" className="left">
          Tx
        </th>
      </tr>
    </thead>
  )
}
